/**
 * Canonical section surface themes and their Tailwind class mappings.
 *
 * Studio modules that expose a theme selector should resolve to one of these
 * keys (or a documented alias) so the same token always paints the same surface.
 *
 * Canonical tokens:
 * - `light`    → gray-1 fill
 * - `white`    → cloud fill (heroes)
 * - `charcoal` → charcoal fill
 * - `purple`   → cipher fill
 *
 * Legacy aliases (normalized via `normalizeSectionTheme`):
 * - `dark` / `black` → charcoal
 * - `gray` → light
 */

export const SECTION_THEMES = ['light', 'white', 'charcoal', 'purple'] as const;

export type SectionTheme = (typeof SECTION_THEMES)[number];

/** Incoming CMS values before alias normalization. */
export type SectionThemeInput = SectionTheme | 'dark' | 'black' | 'gray';

const THEME_ALIASES: Record<string, SectionTheme> = {
  dark: 'charcoal',
  black: 'charcoal',
  gray: 'light',
};

/** Dark surfaces: light text + electric accents. */
export const DARK_SECTION_THEMES = new Set<SectionTheme>([
  'purple',
  'charcoal',
]);

/**
 * Background + base text classes per canonical theme token.
 */
export const SECTION_THEME_SURFACE: Record<SectionTheme, string> = {
  light: 'bg-grayscale-01 text-charcoal',
  white: 'bg-cloud text-charcoal',
  charcoal: 'bg-charcoal text-cloud',
  purple: 'bg-cipher text-cloud',
};

export function normalizeSectionTheme(
  theme: string | null | undefined,
  fallback: SectionTheme = 'light',
): SectionTheme {
  if (!theme) return fallback;
  if (theme in THEME_ALIASES) return THEME_ALIASES[theme]!;
  if ((SECTION_THEMES as readonly string[]).includes(theme)) {
    return theme as SectionTheme;
  }
  return fallback;
}

export function isDarkSectionTheme(theme: string | null | undefined): boolean {
  return DARK_SECTION_THEMES.has(normalizeSectionTheme(theme));
}

/**
 * Dark-theme overrides for modules whose theme selector uses its own
 * vocabulary instead of the canonical tokens above. Without an entry here,
 * unknown theme values normalize to `light` and the nav silently fails to
 * invert over a dark band. Any new module with custom theme values must
 * register the ones that paint a dark section surface.
 */
const MODULE_DARK_THEME_OVERRIDES: Record<string, Set<string>> = {
  // "electric" is a charcoal band with lime cards; only whiteCipher is light.
  stats: new Set(['charcoal', 'electric', 'purple']),
};

/**
 * Whether a module's section band is a dark surface, given its `_type` and
 * raw CMS theme value. Falls back to the canonical-token check for modules
 * without an override entry.
 */
export function isDarkModuleTheme(
  sectionType: string,
  theme: string | null | undefined,
): boolean {
  const override = MODULE_DARK_THEME_OVERRIDES[sectionType];
  if (override) return override.has(theme ?? '');
  return isDarkSectionTheme(theme);
}

/**
 * Which dark nav surface a module paints, for logo / header chrome.
 * Purple bands get a white logo; charcoal (and custom dark hex bands) get Cipher 5.
 */
export function getNavDarkSurface(
  sectionType: string,
  theme: string | null | undefined,
): 'purple' | 'charcoal' | null {
  if (!isDarkModuleTheme(sectionType, theme)) return null;

  if (sectionType === 'stats') {
    return theme === 'purple' ? 'purple' : 'charcoal';
  }

  return normalizeSectionTheme(theme) === 'purple' ? 'purple' : 'charcoal';
}

export function getSectionThemeSurface(
  theme: string | null | undefined,
): string {
  return SECTION_THEME_SURFACE[normalizeSectionTheme(theme)];
}

export function getSectionThemeAccent(
  theme: string | null | undefined,
): 'text-electric' | 'text-cipher' {
  return isDarkSectionTheme(theme) ? 'text-electric' : 'text-cipher';
}

export function getSectionButtonTheme(
  theme: string | null | undefined,
): 'dark' | 'light' {
  return isDarkSectionTheme(theme) ? 'dark' : 'light';
}
